import { useState, useEffect, ReactElement } from "react";
import styled from "styled-components";
import {
	ChallengeScheme,
	ErrorScheme,
	Product,
	ProductScheme,
	Track,
} from "client-v2";
import { useSelector } from "react-redux";
import { State } from "../../../redux";

import {
	Text,
	Color,
	Tabs,
	ColorV2,
	Button,
	InputRange,
	ProgressBar,
	InputPrice,
} from "@adoptaunabuelo/react-components";
import { ArrowUpRight, ChevronDown, Gift } from "lucide-react";
import AmountModal from "../../modal/AmountModal";
import ReliefModal from "../../modal/ReliefModal";
import media from "styled-media-query";
import SectionContainer from "../../container/SectionContainer";
import SectionProgressBar from "../../progress/SectionProgressBar";
import PaymentBreakdownModal from "../../modal/PaymentBreakdownModal";
import SkipModal from "../../modal/SkipPaymentModal";

const DataContainer = styled.div<{ $center: boolean }>`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: ${(props) => (props.$center ? "center" : "flex-start")};
	${media.lessThan("small")`
		min-height: calc(100vh - 58px - 72px - 120px);
	`}
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const AmountView = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	${media.lessThan("medium")`
		flex: 1;
		justify-content: center;
	`}
`;
const PriceView = styled.div<{ $disabled: boolean }>`
	display: flex;
	flex-direction: row;
	cursor: ${(props) => !props.$disabled && "pointer"};
	align-items: center;
	transform: scale(1);
	transition: transform 0.1s ease-in, background-color 0.1s ease-in;
	&:hover {
		transform: ${(props) => !props.$disabled && "scale(1.1)"};
	}
`;
const IconView = styled.div`
	position: absolute;
	bottom: 0px;
	right: -30px;
`;
const ProgressContainer = styled.div`
	display: flex;
	height: 100%;
	justify-content: center;
	flex-direction: column;
	${media.lessThan("medium")`
		margin-bottom: 24px;
	`}
`;
const SkipButtonContainer = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	height: 100%;
	${media.lessThan("medium")`
		justify-content: flex-start;
	`}
`;
const InputContainer = styled.div`
	background-color: white;
	border-radius: 12px;
	padding: 16px;
	margin-top: 52px;
	border: 1px solid ${ColorV2.border.neutralSoft};
	${media.lessThan("medium")`
		margin-top: 0px;
	`}
`;
const PriceToast = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	position: absolute;
	left: 0px;
	right: 0px;
	top: -94px;
`;
const PriceToastTriangle = styled.div`
	width: 0;
	height: 0;
	border-left: 20px solid transparent;
	border-right: 20px solid transparent;
	border-top: 14px solid ${ColorV2.surface.primary};
	margin-top: -4px;
`;
const RelativeView = styled.div`
	position: relative;
`;
const ChallengeContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 32px;
	${media.lessThan("medium")`
        margin-top: 8px;
    `}
`;
const InputPriceView = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: center;
`;

const PlanStep = (props: PlanStepProps & (DonationProps | ChallengeProps)) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const appLocation = useSelector((state: State) => state.location.location);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const translation = useSelector(
		(state: State) => state.location.translation
	);

	const [loading, setLoading] = useState(false);
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [reliefAmount, setReliefAmount] = useState("0");
	const [selectedPrice, setSelectedPrice] = useState<number>(15);
	const [selectedTab, setSelectedTab] = useState<
		{ id: string; title: string } | undefined
	>(undefined);
	const [showAmountModal, setShowAmountModal] = useState(false);
	const [showPriceModal, setShowPriceModal] = useState(false);
	const [tabOptions, setTabsOptions] = useState<
		Array<{ id: string; title: string }>
	>([
		{
			id: "unique",
			title: "Puntual",
		},
	]);
	const [showSkipModal, setShowSkipModal] = useState(false);
	const [charge, setCharge] = useState<"one" | "recurrent">("one");
	const [minPrice, setMinPrice] = useState({
		text: "",
		amount: 0,
	});
	const reliefAmountParts = reliefAmount.split(".");

	useEffect(() => {
		//Track to Mixpanel
		Track.event({
			event: "donation progress init",
			user: currentUser,
			params: {
				view: props.view,
			},
		});
	}, []);

	useEffect(() => {
		//Set initial price
		setSelectedPrice(
			appLocation ? appLocation.currency.price.monthly.default : 20
		);

		//Set min price
		const minPrice = appLocation?.currency.price.monthly.inputOptions
			? appLocation.currency.price.monthly.inputOptions[0]
			: 9;
		const currencyName = appLocation?.currency.name || "eur";
		const language = appLocation?.language || "es-ES";

		setMinPrice({
			text: new Intl.NumberFormat(language, {
				style: "currency",
				currency: currencyName,
				maximumFractionDigits: 0,
			}).format(minPrice),
			amount: minPrice,
		});
	}, [appLocation]);

	useEffect(() => {
		if (props.options && props.options.length > 0) {
			const temp: any = props.options.map((item) => {
				if (item === "monthly")
					return {
						id: "monthly",
						title:
							appLocation?.language === "en"
								? "Monthly"
								: "Mensual",
					};
				else if (item === "annually")
					return {
						id: "annually",
						title:
							appLocation?.language === "en"
								? "Annually"
								: "Anual",
					};
				else if (item === "unique")
					return {
						id: "unique",
						title:
							appLocation?.language === "en"
								? "Unique"
								: "Puntual",
					};
			});
			setTabsOptions(temp);
			setSelectedTab(temp[0]);
		} else if (props.challenge) {
			setSelectedTab({ id: "unique", title: "Puntual" });
		}
	}, [props.options?.length, props.challenge]);

	useEffect(() => {
		if (props.defaultProduct) {
			setSelectedTab({ id: props.defaultProduct.charge, title: "test" });
			setSelectedPrice(props.defaultProduct.price);
		}
	}, [props.defaultProduct]);

	useEffect(() => {
		setCharge(
			props.view === "donation" ||
				props.view === "signature" ||
				props.view === "letter" ||
				props.view === "register"
				? "recurrent"
				: "one"
		);
	}, [props.view]);

	//On tab change
	useEffect(() => {
		if (selectedTab && selectedTab.id) {
			if (selectedTab.id === "annually") {
				const defaultPrice = appLocation
					? appLocation.currency.price.annually.default
					: 120;
				setSelectedPrice(defaultPrice);
			} else if (selectedTab.id === "monthly") {
				const defaultPrice = appLocation
					? appLocation.currency.price.monthly.default
					: 15;
				setSelectedPrice(defaultPrice);
			} else {
				const defaultPrice = appLocation
					? appLocation.currency.price.one.default
					: 50;
				setSelectedPrice(defaultPrice);
			}
		}
	}, [selectedTab]);

	//On price change
	useEffect(() => {
		if (selectedPrice) {
			const totalAmount =
				selectedTab?.id === "monthly"
					? selectedPrice * 12
					: selectedPrice;
			const reliefSection1 = totalAmount > 250 ? 250 : totalAmount;
			const reliefSection2 = totalAmount > 250 ? totalAmount - 250 : 0;
			const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
			setReliefAmount(
				(relief / (selectedTab?.id === "monthly" ? 12 : 1)).toFixed(2)
			);
		}
	}, [selectedPrice]);

	const calculateAmount = (amount: number): number => {
		const tempAmount =
			amount / (appLocation ? appLocation.currency.conversion : 1);
		return tempAmount;
	};

	const getSelectorOptionsPrice = (charge: string) => {
		let result = [];
		if (charge === "monthly") {
			result =
				appLocation && appLocation.currency.price.monthly.inputOptions
					? appLocation.currency.price.monthly.inputOptions.map(
							(i) => ({
								price: i,
							})
					  )
					: [{ price: 9 }, { price: 15 }, { price: 20 }];
		} else if (charge === "annually") {
			result =
				appLocation && appLocation.currency.price.annually.inputOptions
					? appLocation.currency.price.annually.inputOptions.map(
							(i) => ({
								price: i,
							})
					  )
					: [{ price: 90 }, { price: 150 }, { price: 250 }];
		} else {
			result =
				appLocation && appLocation.currency.price.one.inputOptions
					? appLocation.currency.price.one.inputOptions.map((i) => ({
							price: i,
					  }))
					: [{ price: 90 }, { price: 150 }, { price: 250 }];
		}
		if (props.design === "secondary") {
			return result.map((item, index) => {
				if (index === 0) {
					return {
						...item,
						data: [
							{
								title: "Nos ayudas a acompañar a 1 persona mayor",
								icon: "Heart",
							},
						],
					};
				} else if (index === 1) {
					return {
						...item,
						data: [
							{
								title: "Nos ayudas a acompañar a 1 persona mayor",
								icon: "Heart",
							},
							{
								title: "Celebraremos el cumpleaños de 1 persona mayor",
								icon: "Cake",
							},
						],
					};
				} else {
					return {
						...item,
						data: [
							{
								title: "Nos ayudas a acompañar a 1 persona mayor",
								icon: "Heart",
							},
							{
								title: "Celebraremos el cumpleaños de 1 persona mayor",
								icon: "Cake",
							},
							{
								title: "Cumpliremos el sueño de 1 persona mayor cada mes",
								icon: "Rocket",
							},
						],
					};
				}
			});
		}
		return result;
	};

	const onNextClick = (price?: number, charge?: string) => {
		if (selectedTab?.id === "unique" || charge === "unique") {
			//Track to Mixpanel
			Track.event({
				event: "donation product selected",
				user: currentUser,
				params: {
					Product: "one-off",
					amount: price ? price : selectedPrice,
					view: props.view,
				},
			});

			props.onFinish({
				data: {
					objectId: "unique",
					charge: "unique",
					price: price ? price : selectedPrice,
				} as ProductScheme,
			});
		} else {
			setLoading(true);
			Product.get({
				price: price ? price : selectedPrice,
				charge: charge ? charge : selectedTab?.id,
				active: true,
				type: "subscription",
				currency: appLocation ? appLocation.currency.name : "eur",
			})
				.then((result) => {
					if (result.data && result.data.length > 0) {
						//Track to Mixpanel
						const product = result.data[0];
						Track.event({
							event: "donation product selected",
							user: currentUser,
							params: {
								Product: product.objectId,
								amount: selectedPrice,
								view: props.view,
							},
						});

						setLoading(false);
						props.onFinish({ data: product });
					} else {
						setLoading(false);
						props.onFinish({
							error: translation.payment_logic_product_error,
						});
					}
				})
				.catch((e: ErrorScheme) => {
					setLoading(false);
					props.onFinish({ error: e.message });
				});
		}
	};

	const onPriceChange = (e: any) => {
		const value =
			typeof e === "string" ? parseInt(e) : parseInt(e.target.value);
		setSelectedPrice(value);
	};

	return (
		<SectionContainer
			title={props.title}
			childrenProps={{ style: { flex: 1, margin: 0 } }}
			buttonProps={{
				backgroundColor: isMobile
					? ColorV2.surface.background
					: "white",
				LeftView: props.onSkip && (
					<SkipButtonContainer
						style={{
							justifyContent: isMobile
								? "flex-start"
								: props.design === "secondary"
								? "center"
								: "flex-start",
						}}
					>
						<Button
							design={"call-to-action"}
							loading={loading}
							style={{ color: ColorV2.text.neutralHard }}
							onClick={() => setShowSkipModal(true)}
						>
							{
								translation.component_modal_skip_payment_button_skip
							}
						</Button>
					</SkipButtonContainer>
				),
				style: {
					flex: isMobile
						? "unset"
						: props.design === "secondary"
						? 1
						: "unset",
				},
				loading: loading,
				children: translation.component_button_next,
				onClick: () => onNextClick(),
			}}
		>
			<SkipModal
				isVisible={showSkipModal}
				type={isMobile ? "full-screen" : "default"}
				charge={charge}
				video={
					"https://parsefiles-spain.back4app.com/QY5gVZmeai8ug19nHGA9AoFmuPU26k04tGJeV1wT/a49c9c56caa6ddd4b1502f2f14fce17b_welcome_01-mp4.mp4"
				}
				minPrice={minPrice.amount}
				onVideoEnded={() => {
					Track.event({
						event: "donation video finish",
						user: currentUser,
						params: {
							view: props.view,
						},
					});
				}}
				onClick={(product) => {
					Track.event({
						event: "skip donation modal click",
						user: currentUser,
						params: {
							view: props.view,
						},
					});
					setShowSkipModal(false);
					onNextClick(product.price, product.charge);
				}}
				onClose={() => {
					Track.event({
						event: "skip donation modal close",
						user: currentUser,
						params: {
							view: props.view,
						},
					});
					setShowSkipModal(false);
				}}
				onSkip={() => {
					setShowSkipModal(false);
					Track.event({
						event: "skip donation modal close",
						user: currentUser,
						params: {
							view: props.view,
						},
					});
					props.onSkip && props.onSkip();
				}}
				{...props.skipModalProps}
			>
				{props.view === "letter" ? (
					<>
						<Text type="p" style={{ textAlign: "left" }}>
							Como dice Luci, somos una Fundación independiente y
							no recibimos subvenciones, por lo que seguir
							adelante en 2026 depende de personas comprometidas
							con los mayores como tú.
						</Text>
						<Text
							type="p"
							style={{ textAlign: "left", marginTop: 12 }}
						>
							Hazte embajador con una pequeña aportación mensual y{" "}
							<span
								style={{
									color: ColorV2.text.primary,
									fontWeight: 600,
								}}
							>
								ayúdanos a que este proyecto siga vivo.
							</span>{" "}
							Además, ¡te devolverán hasta el 80% de tu donación
							en la Declaración de la Renta!
						</Text>
					</>
				) : (
					<Text type="p" style={{ textAlign: "left" }}>
						Sabemos que no siempre es fácil colaborar, pero con{" "}
						<span
							style={{
								color: ColorV2.text.primary,
								fontWeight: 600,
							}}
						>
							{charge === "one"
								? "solo 5 €"
								: `solo ${minPrice.text} al mes`}
						</span>{" "}
						{props.view === "birthday"
							? "nos ayudas a que el cumpleaños sea inolvidable."
							: props.view === "signature"
							? "puedes hacer que llevemos a cabo más iniciativas como ésta para mejorar la vida de nuestros mayores."
							: "puedes hacer que acompañemos a más personas mayores."}
					</Text>
				)}
			</SkipModal>
			<AmountModal
				isVisible={showAmountModal}
				currencySimbol={appLocation ? appLocation.currency.symbol : "€"}
				onClick={onPriceChange}
				onClose={() => setShowAmountModal(false)}
			/>
			<ReliefModal
				isVisible={showReliefModal}
				price={selectedPrice}
				charge={selectedTab ? selectedTab.id : "monthly"}
				onClose={() => setShowReliefModal(false)}
			/>
			<PaymentBreakdownModal
				isVisible={showPriceModal}
				type={isMobile ? "full-screen" : "default"}
				challenge={props.challenge}
				onClose={() => setShowPriceModal(false)}
			/>
			<DataContainer $center={false}>
				{!(props.title || props.subtitle) && (
					<Text
						type="h3"
						weight="semibold"
						style={{ marginBottom: 12 }}
					>
						{props.title}
					</Text>
				)}
				{props.challenge && props.challenge.type === "birthday" && (
					<ChallengeContainer>
						<SectionProgressBar
							options={props.challenge.distribution}
							defaultProgress={props.challenge.amount}
							progress={selectedPrice + props.challenge.amount}
							animationTime={0.25}
						/>
						<Button
							style={{
								marginTop: 18,
								color: Color.text.high,
								fontSize: 14,
							}}
							design={"call-to-action"}
							icon={
								<ArrowUpRight
									height={18}
									width={18}
									style={{ marginRight: 6 }}
								/>
							}
							onClick={() => setShowPriceModal(true)}
						>
							Cómo se utilizará el dinero del bote
						</Button>
					</ChallengeContainer>
				)}
				{props.challenge && props.challenge.type === "dream" && (
					<ChallengeContainer>
						<ProgressBar
							style={{
								height: 8,
								backgroundColor: ColorV2.border.neutralSoft,
							}}
							progress={[
								{
									value: calculateAmount(
										props.challenge.amount
									),
									color: ColorV2.border.neutralHard,
								},
								{
									value: selectedPrice,
									color: ColorV2.border.primary,
								},
							]}
							minValue={0}
							maxValue={calculateAmount(props.challenge.goal)}
						/>
						<Row
							style={{
								justifyContent: "space-between",
								marginTop: 8,
							}}
						>
							<Col>
								<Text type="h6" weight="medium">
									{calculateAmount(
										props.challenge.amount
									).toFixed(0)}
									{appLocation
										? appLocation.currency.symbol
										: "€"}
								</Text>
								<Text type="p2">Recaudados</Text>
							</Col>
							<Col>
								<Text
									type="h6"
									weight="medium"
									style={{
										color: ColorV2.text.neutralMedium,
									}}
								>
									{calculateAmount(
										props.challenge.goal
									).toFixed(0)}
									{appLocation
										? appLocation.currency.symbol
										: "€"}
								</Text>
								<Text
									type="p2"
									style={{
										color: ColorV2.text.neutralMedium,
									}}
								>
									Objetivo
								</Text>
							</Col>
						</Row>
					</ChallengeContainer>
				)}
				<ProgressContainer>
					{props.amountSelector === "selector" ? (
						<>
							{props.options && props.options.length > 1 && (
								<Tabs
									design="secondary"
									style={{
										width: "100%",
										justifyContent: "center",
										marginTop: 32,
									}}
									cellStyle={{
										maxWidth: 90,
									}}
									options={tabOptions}
									selectedOption={selectedTab}
									textColor={ColorV2.text.neutralHard}
									cellColor={ColorV2.surface.neutralSoft}
									onChange={(option) =>
										setSelectedTab(option)
									}
								/>
							)}
							<InputPriceView>
							<InputPrice
								key={`${appLocation?.currency.name || "eur"}-${selectedTab ? selectedTab.id : "monthly"}`}
								style={
									props.design === "secondary"
										? {
													margin: isMobile ? "32px -24px" : "32px -32px",
													padding: isMobile ? "2px 24px" : "2px 32px",
											  }
											: {}
									}
									currency={
										appLocation
											? appLocation.currency.symbol
											: "€"
									}
									options={getSelectorOptionsPrice(
										selectedTab ? selectedTab.id : "monthly"
									)}
									defaultOption={selectedPrice}
									label={props.subtitle}
									labelValueConversion={
										0.8 *
										(appLocation
											? appLocation.currency.conversion
											: 1)
									}
									hideCustomAmount={true}
									onChange={(price) =>
										setSelectedPrice(price)
									}
								/>
							</InputPriceView>
						</>
					) : (
						<>
							<AmountView>
								<RelativeView>
									{!props.challenge &&
										selectedTab?.id === "unique" &&
										selectedPrice ===
											(appLocation
												? appLocation.currency.price.one
														.max
												: 500) && (
											<PriceToast>
												<Text
													type="p2"
													style={{
														color: "white",
														textAlign: "center",
														borderRadius: 12,
														padding: "8px 16px",
														backgroundColor:
															ColorV2.surface
																.primary,
														border: "4px solid white",
													}}
												>
													{
														translation.payment_unique_max_amount
													}
												</Text>
												<PriceToastTriangle />
											</PriceToast>
										)}
									<Row
										style={{
											justifyContent: "center",
											alignItems: "flex-end",
										}}
									>
										<PriceView
											$disabled={
												selectedTab?.id === "unique"
													? false
													: true
											}
											onClick={() =>
												selectedTab?.id === "unique" &&
												setShowAmountModal(true)
											}
										>
											<Text
												type="d1"
												style={{
													position: "relative",
													fontFamily: "DM Mono",
													textAlign: "center",
													color: Color.text.primary,
												}}
												weight={"semibold"}
											>
												<Text
													type="h4"
													weight="medium"
													style={{
														position: "absolute",
														top: 6,
														right: -75,
														color: Color.text
															.primary,
														textAlign: "left",
														width: 66,
													}}
												>
													{appLocation
														? appLocation.currency
																.symbol
														: "€"}
												</Text>
												{selectedPrice}
											</Text>
											{selectedTab?.id === "unique" && (
												<IconView>
													<ChevronDown
														color={
															ColorV2.text
																.neutralMedium
														}
														height={24}
														width={24}
													/>
												</IconView>
											)}
										</PriceView>
									</Row>
									{props.subtitle &&
										props.type_to_eur &&
										(!props.challenge ||
											(props.challenge.type !==
												"birthday" &&
												props.challenge.type !==
													"dream")) && (
											<Text
												type="p"
												style={{
													textAlign: "center",
													marginTop: 12,
												}}
											>
												{
													props.subtitle.split(
														"{{data}}"
													)[0]
												}
												<span
													style={{ fontWeight: 600 }}
												>
													{(
														props.type_to_eur *
														(appLocation
															? appLocation
																	.currency
																	.conversion
															: 1) *
														selectedPrice
													).toFixed(0)}{" "}
													{props.subtitle_type}
												</span>
												{
													props.subtitle.split(
														"{{data}}"
													)[1]
												}
											</Text>
										)}
								</RelativeView>
							</AmountView>
							<InputContainer>
								<InputRange
									min={
										selectedTab?.id === "annually"
											? appLocation
												? appLocation.currency.price
														.annually.min
												: 50
											: selectedTab?.id === "monthly"
											? appLocation
												? appLocation.currency.price
														.monthly.min
												: 5
											: appLocation
											? appLocation.currency.price.one.min
											: 5
									}
									max={
										props.challenge &&
										props.challenge.type !== "signature"
											? props.challenge.goal -
													props.challenge.amount >
											  250
												? calculateAmount(
														props.challenge.goal -
															props.challenge
																.amount
												  )
												: 250
											: selectedTab?.id === "annually"
											? appLocation
												? appLocation.currency.price
														.annually.max
												: 250
											: selectedTab?.id === "monthly"
											? appLocation
												? appLocation.currency.price
														.monthly.max
												: 40
											: appLocation
											? appLocation.currency.price.one.max
											: 500
									}
									value={selectedPrice}
									unit={
										appLocation
											? appLocation.currency.symbol
											: "€"
									}
									step={
										selectedTab?.id === "annually"
											? appLocation
												? appLocation.currency.price
														.annually.jump
												: 10
											: selectedTab?.id === "monthly"
											? appLocation
												? appLocation.currency.price
														.monthly.jump
												: 1
											: appLocation
											? appLocation.currency.price.one
													.jump
											: 5
									}
									backgroundColor={ColorV2.border.neutralSoft}
									lineColor={ColorV2.surface.primary}
									thumbColor={ColorV2.surface.primary}
									hideRange={true}
									hideLabels={true}
									onChange={onPriceChange}
								/>
								{props.options && props.options.length > 1 && (
									<Tabs
										design="secondary"
										style={{
											width: "100%",
											justifyContent: "center",
											marginTop: 18,
										}}
										cellStyle={{
											maxWidth: 90,
										}}
										options={tabOptions}
										selectedOption={selectedTab}
										textColor={ColorV2.text.neutralHard}
										cellColor={ColorV2.surface.neutralSoft}
										onChange={(option) =>
											setSelectedTab(option)
										}
									/>
								)}
							</InputContainer>
						</>
					)}
					{appLocation &&
						appLocation.country_code === "ES" &&
						appLocation.currency.name === "eur" && (
							<div
								style={{
									marginBottom: isMobile ? 0 : 42,
									marginTop: 16,
								}}
							>
								<Text
									type="c2"
									style={{
										textAlign: "center",
									}}
								>
									{translation.payment_renta_01}
									<span
										style={{
											backgroundColor:
												ColorV2.surface.secondary,
											padding: "1px 6px",
											borderRadius: 3,
											fontWeight: 500,
										}}
									>
										{reliefAmountParts[0]}
										<span>
											{reliefAmountParts[1] !== "00" &&
												"," + reliefAmountParts[1]}
											{" €"}
										</span>
									</span>
									{translation.payment_renta_02}
								</Text>
								<Text
									type="c2"
									style={{
										textAlign: "center",
										marginTop: 4,
										textDecoration: "underline",
										cursor: "pointer",
									}}
									onClick={() => setShowReliefModal(true)}
								>
									{translation.component_text_more_info}
								</Text>
							</div>
						)}
				</ProgressContainer>
			</DataContainer>
			{props.children}
		</SectionContainer>
	);
};
export default PlanStep;
export interface PlanStepProps {
	design?: "primary" | "secondary";
	view: string;
	title: string;
	subtitle?: string;
	type_to_eur?: number;
	subtitle_type?: string;
	children?: ReactElement;
	skipModalProps?: {
		video?: string;
		title?: string;
		duration?: number;
		message?: string;
		hideAlert?: boolean;
	};
	onFinish: (result: { data?: ProductScheme; error?: string }) => void;
	onSkip?: () => void;
}

interface DonationProps {
	defaultProduct?: ProductScheme;
	options: Array<"monthly" | "annually" | "unique">;
	amountSelector: "slider" | "selector";
	challenge?: undefined;
}

interface ChallengeProps {
	amountSelector?: undefined;
	defaultProduct?: undefined;
	options?: undefined;
	challenge: ChallengeScheme;
}
